import React, { useState, useEffect } from "react";
import ConfirmModal from "../components/ConfirmModal";
import { classifyProvisionError } from "../utils/provisionError";

const API = process.env.REACT_APP_API_URL || "";

// Single source of truth for "Xc/YGB" label + badge colors per instance_type
// — the instance-list badge used to guess this from a chain of ternaries
// that only covered the older Sao Paulo types, so every newer type (bk_*)
// silently fell through to the wrong "16c/64GB" default.
const INSTANCE_TYPE_META = {
  spot_8_16:       { label: "8c/16GB",   color: "#38bdf8" },
  spot_8_32:       { label: "8c/32GB",   color: "#fb7185" },
  spot_16_32:      { label: "16c/32GB",  color: "#fbbf24" },
  spot_16_64:      { label: "16c/64GB",  color: "#38bdf8" },
  spot_32_128:     { label: "32c/128GB", color: "#a78bfa" },
  sg_spot_16_64:   { label: "16c/64GB",  color: "#34d399" },
  sg_spot_32_128:  { label: "32c/128GB", color: "#22d3ee" },
  bk_spot_16_64:   { label: "16c/64GB",  color: "#f472b6" },
  bk_spot_4_8:     { label: "4c/8GB",    color: "#fb923c" },
};
const instanceTypeMeta = (type) => INSTANCE_TYPE_META[type] || { label: type || "?", color: "#38bdf8" };

// /tencent/list returns the raw Tencent region code (e.g. "ap-singapore"),
// not a human-readable name — map it for display on each instance card.
const REGION_LABELS = {
  "sa-saopaulo": "🇧🇷 Sao Paulo",
  "ap-singapore": "🇸🇬 Singapore",
  "ap-bangkok": "🇹🇭 Bangkok",
  "ap-jakarta": "🇮🇩 Jakarta",
  "ap-hongkong": "🇭🇰 Hong Kong",
};
const regionLabel = (region) => REGION_LABELS[region] || region || "Unknown";

const StatusBadge = ({ status }) => {
  const map = {
    RUNNING:  { cls: "adm-badge adm-badge-green", label: "● Running" },
    PENDING:  { cls: "adm-badge adm-badge-amber", label: "◌ Pending" },
    STOPPED:  { cls: "adm-badge adm-badge-red",   label: "○ Stopped" },
    STOPPING: { cls: "adm-badge adm-badge-amber", label: "◌ Stopping" },
  };
  const s = map[status] || { cls: "adm-badge adm-badge-gray", label: status };
  return <span className={s.cls}>{s.label}</span>;
};

// Small coloured pill showing which Tencent account owns this instance
const AccountBadge = ({ label }) => {
  if (!label) return null;
  // Cycle through 7 subtle hues so accounts are visually distinct
  const palette = [
    { bg: "rgba(56,189,248,0.12)",  border: "rgba(56,189,248,0.35)",  color: "#38bdf8" },  // sky
    { bg: "rgba(99,102,241,0.12)",  border: "rgba(99,102,241,0.35)",  color: "#818cf8" },  // indigo
    { bg: "rgba(34,197,94,0.12)",   border: "rgba(34,197,94,0.35)",   color: "#4ade80" },  // green
    { bg: "rgba(251,191,36,0.12)",  border: "rgba(251,191,36,0.35)",  color: "#fbbf24" },  // amber
    { bg: "rgba(244,114,182,0.12)", border: "rgba(244,114,182,0.35)", color: "#f472b6" },  // pink
    { bg: "rgba(20,184,166,0.12)",  border: "rgba(20,184,166,0.35)",  color: "#2dd4bf" },  // teal
    { bg: "rgba(168,85,247,0.12)",  border: "rgba(168,85,247,0.35)",  color: "#c084fc" },  // purple
  ];
  // Derive index from the trailing digit in label (e.g. "Account-3" → 2)
  const num   = parseInt((label.match(/\d+$/) || ["1"])[0], 10);
  const style = palette[(num - 1) % palette.length];
  return (
    <span style={{
      fontSize: 10, fontWeight: 800, letterSpacing: "0.8px",
      textTransform: "uppercase", padding: "2px 8px", borderRadius: 99,
      background: style.bg, border: `1px solid ${style.border}`, color: style.color,
      fontFamily: "'Outfit', sans-serif",
    }}>
      {label}
    </span>
  );
};

const STAGE_LABELS = {
  booting:             "Booting instance...",
  installing_deps:     "Installing dependencies...",
  installing_chromium: "Installing Chromium...",
  starting_service:    "Starting agent service...",
  online:              "Online",
  failed:              "Install failed",
};

const ProvisionBar = ({ stage, pct, error }) => {
  if (!stage || stage === "online") return null;

  const isFailed = stage === "failed";
  const barPct   = isFailed ? 100 : (pct || 0);
  const label    = STAGE_LABELS[stage] || stage;

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
        <span style={{ fontSize: 11, color: isFailed ? "#f87171" : "#94a3b8", fontWeight: 600 }}>{label}</span>
        {!isFailed && <span style={{ fontSize: 11, color: "#4a6280", fontWeight: 700 }}>{barPct}%</span>}
      </div>
      <div style={{ height: 5, borderRadius: 99, background: "rgba(255,255,255,0.07)", overflow: "hidden" }}>
        <div style={{
          height: "100%", borderRadius: 99, width: `${barPct}%`,
          background: isFailed
            ? "linear-gradient(90deg,#ef4444,#b91c1c)"
            : "linear-gradient(90deg,#38bdf8,#6366f1)",
          transition: "width .6s ease",
          boxShadow: isFailed ? "0 0 8px #ef444466" : "0 0 8px #38bdf866",
        }} />
      </div>
      {isFailed && error && (
        <div style={{
          marginTop: 8, background: "rgba(127,29,29,0.35)",
          border: "1px solid rgba(239,68,68,0.3)", borderRadius: 8,
          padding: "8px 10px", fontSize: 11, color: "#fca5a5",
          fontFamily: "'JetBrains Mono', monospace", wordBreak: "break-word", lineHeight: 1.5,
        }}>
          {classifyProvisionError(error) && (
            <div style={{ fontWeight: 700, marginBottom: 4, fontFamily: "inherit" }}>
              {classifyProvisionError(error).label}
            </div>
          )}
          {error}
        </div>
      )}
    </div>
  );
};

const GhostCard = () => (
  <div className="inst-card ghost-card">
    <div className="ghost-row" style={{ width: "60%", height: 14, marginBottom: 14 }} />
    <div className="ghost-row" style={{ width: "40%", height: 10, marginBottom: 8 }} />
    <div className="ghost-row" style={{ width: "70%", height: 13, marginBottom: 14 }} />
    <div className="ghost-row" style={{ width: "50%", height: 10, marginBottom: 8 }} />
    <div className="ghost-row" style={{ width: "85%", height: 13, marginBottom: 16 }} />
    <div className="ghost-row" style={{ width: "100%", height: 32, borderRadius: 8 }} />
  </div>
);

export default function Tencent() {
  const [instances, setInstances]     = useState([]);
  const [accountsCount, setAccountsCount] = useState(0);
  const [totalSlots, setTotalSlots]     = useState(0);
  const [loading, setLoading]         = useState(false);
  const [creating, setCreating]       = useState(false);
  const [ghostCount, setGhostCount]   = useState(0);
  const [countInput, setCountInput]   = useState("1");
  const [countErr, setCountErr]       = useState("");
  const [selected, setSelected]       = useState(new Set());
  const [toast, setToast]             = useState(null);
  const [confirmAll, setConfirmAll]   = useState(false);
  const [deletingIds, setDeletingIds] = useState(new Set());
  const [retryingIds, setRetryingIds] = useState(new Set());
  const [confirmDelete, setConfirmDelete] = useState(null); // { id, name } or 'selected' or 'all'
  const [confirmSelected, setConfirmSelected] = useState(false);
  const [autoTerminate, setAutoTerminate]     = useState(true);
  const [togglingAT, setTogglingAT]           = useState(false);
  const [manualType, setManualType]           = useState("spot_16_32");
  const [manualCount, setManualCount]         = useState("1");
  const [manualCreating, setManualCreating]   = useState(false);
  const [accounts, setAccounts]               = useState([]); // [{id,label,max_instances}]
  const [capValue, setCapValue]               = useState(12);
  const [capApplying, setCapApplying]         = useState(false);
  const [confirmCap, setConfirmCap]           = useState(false);
  const [addAcctModal, setAddAcctModal]       = useState(false);
  const [acctForm, setAcctForm]               = useState({
    label: "", secret_id: "", secret_key: "", max_instances: "12",
    security_group_id: "", security_group_id_sg: "", security_group_id_bk: "",
    security_group_id_jk: "", security_group_id_hk: "",
  });
  const [acctSaving, setAcctSaving]           = useState(false);
  const [acctErr, setAcctErr]                 = useState("");
  const token = () => localStorage.getItem("token") || "";

  const showToast = (msg, type = "success") => { // type: success | error | warn
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadInstances = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      const r = await fetch(`${API}/tencent/list`, {
        headers: { Authorization: "Bearer " + token() }
      });
      const d = await r.json();
      if (d.success) { setInstances(d.instances); setAccountsCount(d.accounts_count || 0); setTotalSlots(d.total_slots || 0); }
    } catch {
      showToast("Failed to load instances", "error");
    } finally {
      setLoading(false);
    }
  };





  const loadSettings = async () => {
    try {
      const r = await fetch(`${API}/tencent/settings`, {
        headers: { Authorization: "Bearer " + token() }
      });
      const d = await r.json();
      if (typeof d.auto_terminate_enabled === "boolean") setAutoTerminate(d.auto_terminate_enabled);
    } catch {}
  };
  const toggleAutoTerminate = async () => {
    setTogglingAT(true);
    try {
      const newVal = !autoTerminate;
      const r = await fetch(`${API}/tencent/settings`, {
        method: "POST",
        headers: { Authorization: "Bearer " + token(), "Content-Type": "application/json" },
        body: JSON.stringify({ auto_terminate_enabled: newVal })
      });
      const d = await r.json();
      if (d.success) {
        setAutoTerminate(newVal);
        showToast(newVal ? "✅ Auto-terminate ON — Idle instances 10 min baad terminate honge" : "🔒 Auto-terminate OFF — Instances aur agents terminate nahi honge", newVal ? "success" : "warn");
      }
    } catch {
      showToast("Settings update failed", "error");
    } finally {
      setTogglingAT(false);
    }
  };
  const handleRetry = async (instanceId) => {
    setRetryingIds(p => new Set(p).add(instanceId));
    try {
      const r = await fetch(`${API}/tencent/retry/${instanceId}`, {
        method: "POST",
        headers: { Authorization: "Bearer " + token() }
      });
      const d = await r.json();
      if (d.success) {
        showToast("Retry started — re-attempting install over SSH");
        loadInstances(false);
      } else {
        showToast(d.detail || "Retry failed", "error");
      }
    } catch {
      showToast("Network error", "error");
    } finally {
      setRetryingIds(p => { const n = new Set(p); n.delete(instanceId); return n; });
    }
  };

  const loadAccounts = async () => {
    try {
      const r = await fetch(`${API}/tencent/accounts`, {
        headers: { Authorization: "Bearer " + token() }
      });
      const d = await r.json();
      if (d.success) {
        setAccounts(d.accounts);
        if (d.accounts.length > 0) setCapValue(d.accounts[0].max_instances);
      }
    } catch {}
  };

  const doApplyCap = async () => {
    setCapApplying(true);
    try {
      const r = await fetch(`${API}/tencent/accounts/cap`, {
        method: "PATCH",
        headers: { Authorization: "Bearer " + token(), "Content-Type": "application/json" },
        body: JSON.stringify({ max_instances: capValue })
      });
      const d = await r.json();
      if (d.success) {
        showToast(`✅ Updated ${d.updated} account(s) to cap=${d.max_instances}`);
        loadAccounts();
        loadInstances(false);
      } else {
        showToast(d.detail || "Cap update failed", "error");
      }
    } catch {
      showToast("Network error", "error");
    } finally {
      setCapApplying(false);
      setConfirmCap(false);
    }
  };

  const handleAddAccount = async () => {
    setAcctErr("");
    if (!acctForm.label.trim() || !acctForm.secret_id.trim() || !acctForm.secret_key.trim()) {
      setAcctErr("Label, Secret ID aur Secret Key required hain");
      return;
    }
    setAcctSaving(true);
    try {
      const r = await fetch(`${API}/tencent/accounts`, {
        method: "POST",
        headers: { Authorization: "Bearer " + token(), "Content-Type": "application/json" },
        body: JSON.stringify({
          label: acctForm.label.trim(),
          secret_id: acctForm.secret_id.trim(),
          secret_key: acctForm.secret_key.trim(),
          max_instances: Number(acctForm.max_instances) || 12,
          security_group_id: acctForm.security_group_id.trim() || null,
          security_group_id_sg: acctForm.security_group_id_sg.trim() || null,
          security_group_id_bk: acctForm.security_group_id_bk.trim() || null,
          security_group_id_jk: acctForm.security_group_id_jk.trim() || null,
          security_group_id_hk: acctForm.security_group_id_hk.trim() || null,
        })
      });
      const d = await r.json();
      if (d.success) {
        showToast(`✅ Account "${d.label}" added`);
        setAddAcctModal(false);
        setAcctForm({ label: "", secret_id: "", secret_key: "", max_instances: "12",
          security_group_id: "", security_group_id_sg: "", security_group_id_bk: "",
          security_group_id_jk: "", security_group_id_hk: "" });
        loadAccounts();
      } else {
        setAcctErr(d.detail || "Account add nahi ho paya");
      }
    } catch {
      setAcctErr("Network error");
    } finally {
      setAcctSaving(false);
    }
  };

  useEffect(() => { loadInstances(); loadSettings(); loadAccounts(); }, []);

  // Poll every 6s while any instance is still provisioning or pending
  useEffect(() => {
    const hasActive = instances.some(i =>
      i.status === "PENDING" ||
      (i.provision_stage && i.provision_stage !== "online" && i.provision_stage !== "failed")
    );
    if (!hasActive) return;
    const t = setTimeout(loadInstances, 6000);
    return () => clearTimeout(t);
  }, [instances]);

  const handleCountChange = (val) => {
    setCountInput(val);
    if (val.trim() === "") { setCountErr("Enter a number"); return; }
    if (!/^\d+$/.test(val.trim())) { setCountErr("Whole numbers only"); return; }
    const n = parseInt(val, 10);
    if (n < 1) { setCountErr("Must be at least 1"); return; }
    setCountErr("");
  };

  const parsedCount = () => {
    const n = parseInt(countInput, 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  };

  const handleCreate = async () => {
    const n = parsedCount();
    if (!n || countErr) { showToast(countErr || "Enter a valid count", "error"); return; }
    setCreating(true);
    setGhostCount(n);
    try {
      const r = await fetch(`${API}/tencent/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token() },
        body: JSON.stringify({ count: n, name_prefix: "BotCrowd-Linux" })
      });
      const d = await r.json();
      if (d.success) {
        showToast(`${d.count} Ubuntu instance${d.count > 1 ? "s" : ""} created successfully!`);
        await loadInstances();
      } else {
        showToast(d.detail || "Creation failed", "error");
      }
    } catch {
      showToast("Network error", "error");
    } finally {
      setCreating(false);
      setGhostCount(0);
    }
  };

  const handleCreateManual = async () => {
    const n = parseInt(manualCount, 10);
    if (!n || n < 1) { showToast("Enter a valid count", "error"); return; }
    setManualCreating(true);
    try {
      const r = await fetch(`${API}/tencent/create-manual`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token() },
        body: JSON.stringify({ count: n, instance_type: manualType, name_prefix: "BotCrowd-Linux" })
      });
      const d = await r.json();
      if (d.success) {
        showToast(`✅ ${d.count} manual instance(s) [${manualType}] created! Online in ~2 mins.`);
        await loadInstances();
      } else {
        showToast(d.detail || d.message || "Creation failed", "error");
      }
    } catch {
      showToast("Network error", "error");
    } finally {
      setManualCreating(false);
    }
  };

  const handleDeleteOne = async (id) => {
    setConfirmDelete(null);
    // Show deleting state — do NOT remove optimistically (causes black screen)
    setDeletingIds(prev => new Set([...prev, id]));
    try {
      const r = await fetch(`${API}/tencent/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token() },
        body: JSON.stringify({ instance_ids: [id] })
      });
      const d = await r.json();
      if (!d.success) {
        showToast(d.detail || "Delete failed", "error");
        await loadInstances(false);
      } else {
        showToast("Instance terminated");
        // Optimistically remove — keep in deletingIds until confirmed gone
        setInstances(prev => prev.filter(i => i.instance_id !== id));
        setSelected(prev => { const s = new Set(prev); s.delete(id); return s; });
        // Poll until instance is actually gone from API
        let attempts = 0;
        const poll = async () => {
          attempts++;
          await loadInstances(false);
          setInstances(prev => {
            const stillThere = prev.find(i => i.instance_id === id);
            if (stillThere && attempts < 10) {
              setTimeout(poll, 3000);
            } else {
              setDeletingIds(p => { const s = new Set(p); s.delete(id); return s; });
            }
            return prev.filter(i => i.instance_id !== id);
          });
        };
        setTimeout(poll, 3000);
      }
    } catch {
      showToast("Delete failed", "error");
    } finally {
      setDeletingIds(prev => { const s = new Set(prev); s.delete(id); return s; });
    }
  };

  const handleDeleteSelected = async () => {
    if (selected.size === 0) { showToast("Select instances first", "error"); return; }
    setConfirmSelected(false);
    const ids = [...selected];
    // NO optimistic remove — show deleting state only (prevents black screen)
    setDeletingIds(new Set(ids));
    try {
      const r = await fetch(`${API}/tencent/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token() },
        body: JSON.stringify({ instance_ids: ids })
      });
      const d = await r.json();
      if (d.success) {
        showToast(`${ids.length} instance(s) terminated`);
        await loadInstances(false); // Refresh without blank screen
        setSelected(new Set());
      } else {
        showToast(d.detail || "Delete failed", "error");
        await loadInstances(false);
      }
    } catch {
      showToast("Delete failed", "error");
      await loadInstances();
    } finally {
      setDeletingIds(new Set());
    }
  };

  const handleDeleteAll = async () => {
    setConfirmAll(false);
    // Show deleting state on all cards
    const allIds = instances.map(i => i.instance_id);
    setDeletingIds(new Set(allIds));
    try {
      const r = await fetch(`${API}/tencent/delete-all`, {
        method: "DELETE",
        headers: { Authorization: "Bearer " + token() }
      });
      const d = await r.json();
      if (d.success) {
        showToast(d.message || `All ${allIds.length} instance(s) terminated`);
        setInstances([]);
        setSelected(new Set());
      } else {
        showToast(d.detail || "Delete all failed", "error");
        await loadInstances(false);
      }
    } catch {
      showToast("Delete all failed", "error");
      await loadInstances(false);
    } finally {
      setDeletingIds(new Set());
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const toggleAll = () => {
    if (selected.size === instances.length) setSelected(new Set());
    else setSelected(new Set(instances.map(i => i.instance_id)));
  };

  const runningCount      = instances.filter(i => i.status === "RUNNING").length;
  const pendingCount      = instances.filter(i => i.status === "PENDING").length;
  const provisioningCount = instances.filter(i =>
    i.provision_stage && i.provision_stage !== "online" && i.provision_stage !== "failed"
  ).length;

  return (
    <div style={{ animation: "fadeIn .2s ease" }}>
      <style>{`
        .tencent-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 14px; }
        .inst-card { background: rgba(8,13,32,0.72); backdrop-filter: blur(28px) saturate(1.5); border: 1px solid rgba(255,255,255,0.09); border-radius: 16px; padding: 18px; box-shadow: 0 8px 48px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.06); transition: border-color .25s, box-shadow .25s, transform .2s; }
        .inst-card:hover { border-color: rgba(99,179,237,0.2); box-shadow: 0 14px 58px rgba(0,0,0,.65), 0 0 0 1px rgba(99,179,237,0.08), inset 0 1px 0 rgba(255,255,255,.06); }
        .inst-card.selected { border-color: rgba(56,189,248,0.45); background: rgba(56,189,248,0.05); }
        .inst-card.provisioning { border-color: rgba(99,102,241,0.3); }
        .inst-card.failed { border-color: rgba(239,68,68,0.3); }
        .inst-meta-label { font-size: 10px; color: #334155; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 3px; font-weight: 700; }
        .inst-ip { font-family: 'JetBrains Mono', monospace; font-size: 13px; color: #38bdf8; font-weight: 600; }
        .inst-id { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: #4a6280; }

        .ghost-card { display: flex; flex-direction: column; pointer-events: none; }
        .ghost-row { background: linear-gradient(90deg, rgba(255,255,255,0.05) 25%, rgba(255,255,255,0.1) 37%, rgba(255,255,255,0.05) 63%); background-size: 400% 100%; animation: ghostShimmer 1.4s ease infinite; border-radius: 6px; }
        @keyframes ghostShimmer { 0% { background-position: 100% 50%; } 100% { background-position: 0 50%; } }

        .mini-stat { background: rgba(8,13,32,0.72); backdrop-filter: blur(28px) saturate(1.5); border: 1px solid rgba(255,255,255,0.09); border-radius: 14px; padding: 14px 18px; flex: 1; min-width: 110px; }
        .mini-stat-val { font-size: 26px; font-weight: 900; letter-spacing: -1px; line-height: 1; margin-bottom: 4px; }
        .mini-stat-lbl { font-size: 10px; letter-spacing: 1.4px; text-transform: uppercase; font-weight: 800; color: #4a6280; }

        .count-field-wrap { position: relative; display: flex; align-items: center; }
        .count-field { width: 110px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.13); color: #e2e8f0; border-radius: 9px; padding: 9px 13px; font-size: 15px; font-weight: 700; font-family: 'Outfit', sans-serif; outline: none; transition: border-color .18s, box-shadow .18s; }
        .count-field:focus { border-color: rgba(99,179,237,0.55); box-shadow: 0 0 0 3px rgba(99,179,237,0.09); }
        .count-field.has-err { border-color: rgba(248,113,113,0.5); }
        .count-step { width: 32px; height: 32px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.1); background: rgba(255,255,255,0.05); color: #94a3b8; cursor: pointer; font-size: 16px; display: flex; align-items: center; justify-content: center; transition: all .15s; flex-shrink: 0; }
        .count-step:hover { background: rgba(255,255,255,0.1); color: #e2e8f0; }
        .preset-chip { padding: 6px 13px; border-radius: 99px; border: 1px solid rgba(255,255,255,0.1); background: rgba(255,255,255,0.04); color: #94a3b8; font-size: 12px; font-weight: 700; cursor: pointer; transition: all .15s; font-family: 'Outfit', sans-serif; }
        .preset-chip:hover { border-color: rgba(56,189,248,0.35); color: #38bdf8; background: rgba(56,189,248,0.06); }
        .preset-chip.active { border-color: rgba(56,189,248,0.5); color: #38bdf8; background: rgba(56,189,248,0.1); }

        @media(max-width:600px) {
          .tencent-grid { grid-template-columns: 1fr; }
          .tencent-actions { flex-direction: column !important; gap: 8px !important; }
          .tencent-actions button { width: 100%; justify-content: center; }
          .tencent-create-row { flex-wrap: wrap; gap: 12px !important; }
          .mini-stat-row { flex-wrap: wrap; }
        }
      `}</style>

      {toast && (
        <div style={{ position: "fixed", top: 20, right: 16, zIndex: 9999, background: toast.type === "error" ? "rgba(127,29,29,0.97)" : toast.type === "warn" ? "rgba(120,53,15,0.97)" : "rgba(20,83,45,0.97)", border: `1px solid ${toast.type === "error" ? "#ef4444" : toast.type === "warn" ? "#f59e0b" : "#22c55e"}`, color: "#fff", borderRadius: 12, padding: "12px 20px", boxShadow: "0 4px 24px #0009", fontSize: 14, fontWeight: 600, backdropFilter: "blur(16px)", maxWidth: "calc(100vw - 32px)" }}>{toast.msg}</div>
      )}

      {confirmAll && (
        <div className="adm-modal-bg open" onClick={() => setConfirmAll(false)}>
          <div className="adm-modal" onClick={e => e.stopPropagation()}>
            <div className="adm-modal-head">
              <span className="adm-modal-title">Delete All Instances?</span>
              <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => setConfirmAll(false)}>X</button>
            </div>
            <p style={{ color: "#94a3b8", fontSize: 14, lineHeight: 1.6 }}>
              This will permanently terminate <strong style={{ color: "#f87171" }}>ALL {instances.length} instance(s)</strong> across all accounts. This cannot be undone.
            </p>
            <div className="adm-modal-footer">
              <button className="adm-btn" onClick={() => setConfirmAll(false)}>Cancel</button>
              <button className="adm-btn adm-btn-danger" onClick={handleDeleteAll}>Delete All</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 20, display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: "#f1f5f9", margin: 0 }}>Tencent Cloud</h2>
          <p style={{ color: "#4a6280", fontSize: 13, margin: "4px 0 0" }}>Singapore · SA5.4XLARGE64 · 16 cores 64GB · Spot · Ubuntu 22.04 · {accountsCount} accounts · {totalSlots} slots</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, position: "relative", zIndex: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "6px 12px" }}>
            <span style={{ fontSize: 12, color: autoTerminate ? "#4ade80" : "#f87171", fontWeight: 700 }}>
              {autoTerminate ? "⏱ Auto-Terminate ON" : "🔒 Auto-Terminate OFF"}
            </span>
            <div
              onClick={togglingAT ? undefined : toggleAutoTerminate}
              style={{
                width: 38, height: 20, borderRadius: 99, cursor: togglingAT ? "not-allowed" : "pointer",
                background: autoTerminate ? "#4ade80" : "#475569",
                position: "relative", transition: "background 0.2s", opacity: togglingAT ? 0.6 : 1
              }}
            >
              <div style={{
                position: "absolute", top: 3, left: autoTerminate ? 20 : 3,
                width: 14, height: 14, borderRadius: "50%", background: "#fff",
                transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)"
              }} />
            </div>
          </div>
          <button className="adm-btn adm-btn-sm" onClick={loadInstances} disabled={loading}>{loading ? "..." : "Refresh"}</button>
        </div>
      </div>

      <div className="mini-stat-row" style={{ display: "flex", gap: 14, marginBottom: 20 }}>
        <div className="mini-stat">
          <div className="mini-stat-val" style={{ color: "#f1f5f9" }}>{instances.length}</div>
          <div className="mini-stat-lbl">Total Instances</div>
        </div>
        <div className="mini-stat">
          <div className="mini-stat-val" style={{ color: "#4ade80" }}>{runningCount}</div>
          <div className="mini-stat-lbl">Running</div>
        </div>
        <div className="mini-stat">
          <div className="mini-stat-val" style={{ color: "#fbbf24" }}>{pendingCount}</div>
          <div className="mini-stat-lbl">Pending</div>
        </div>
        {provisioningCount > 0 && (
          <div className="mini-stat">
            <div className="mini-stat-val" style={{ color: "#818cf8" }}>{provisioningCount}</div>
            <div className="mini-stat-lbl">Installing</div>
          </div>
        )}
      </div>

      <div className="adm-card adm-card-pad" style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#cbd5e1", marginBottom: 4, textTransform: "uppercase", letterSpacing: "1px" }}>Global Instance Cap</div>
        <div style={{ fontSize: 12, color: "#4a6280", marginBottom: 14 }}>
          Applies to ALL {accounts.length} Tencent accounts at once. Current: {accounts[0]?.max_instances ?? "—"} per account.
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <input
            type="range" min="6" max="18" value={capValue}
            onChange={e => setCapValue(Number(e.target.value))}
            style={{ flex: "1 1 200px" }}
          />
          <span style={{ fontSize: 22, fontWeight: 800, color: "#38bdf8", minWidth: 36, textAlign: "center" }}>{capValue}</span>
          <button
            className="adm-btn adm-btn-primary"
            disabled={capApplying || accounts.length === 0 || capValue === (accounts[0]?.max_instances ?? capValue)}
            onClick={() => setConfirmCap(true)}
          >
            {capApplying ? "Applying…" : `Apply to all ${accounts.length} accounts`}
          </button>
        </div>
      </div>

      <ConfirmModal
        open={confirmCap}
        title="Apply new cap to ALL accounts?"
        message={`Set max_instances = ${capValue} on all ${accounts.length} Tencent accounts? This changes live provisioning capacity immediately.`}
        confirmLabel="Apply"
        danger={false}
        onConfirm={doApplyCap}
        onCancel={() => setConfirmCap(false)}
      />

      <div className="adm-card adm-card-pad" style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#cbd5e1", marginBottom: 4, textTransform: "uppercase", letterSpacing: "1px" }}>Tencent Accounts</div>
        <div style={{ fontSize: 12, color: "#4a6280", marginBottom: 14 }}>{accounts.length} account(s) configured.</div>
        <button className="adm-btn adm-btn-primary" onClick={() => setAddAcctModal(true)}>+ Add Tencent Account</button>
      </div>

      {addAcctModal && (
        <div className="adm-modal-bg open" onClick={() => setAddAcctModal(false)}>
          <div className="adm-modal" style={{ width: 480 }} onClick={e => e.stopPropagation()}>
            <div className="adm-modal-head">
              <span className="adm-modal-title">Add Tencent Account</span>
              <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => setAddAcctModal(false)}>✕</button>
            </div>
            <div className="adm-form-row">
              <label className="adm-label">Label *</label>
              <input className="adm-input" value={acctForm.label} onChange={e => setAcctForm(f => ({ ...f, label: e.target.value }))} placeholder="e.g. Account-12" />
            </div>
            <div className="adm-form-row">
              <label className="adm-label">Secret ID *</label>
              <input className="adm-input" value={acctForm.secret_id} onChange={e => setAcctForm(f => ({ ...f, secret_id: e.target.value }))} />
            </div>
            <div className="adm-form-row">
              <label className="adm-label">Secret Key *</label>
              <input type="password" className="adm-input" value={acctForm.secret_key} onChange={e => setAcctForm(f => ({ ...f, secret_key: e.target.value }))} />
            </div>
            <div className="adm-form-row">
              <label className="adm-label">Max Instances</label>
              <input type="number" min="1" max="100" className="adm-input" value={acctForm.max_instances} onChange={e => setAcctForm(f => ({ ...f, max_instances: e.target.value }))} />
            </div>
            <div className="adm-form-row">
              <label className="adm-label">Security Group — Sao Paulo (optional)</label>
              <input className="adm-input" value={acctForm.security_group_id} onChange={e => setAcctForm(f => ({ ...f, security_group_id: e.target.value }))} />
            </div>
            <div className="adm-form-row">
              <label className="adm-label">Security Group — Singapore (optional)</label>
              <input className="adm-input" value={acctForm.security_group_id_sg} onChange={e => setAcctForm(f => ({ ...f, security_group_id_sg: e.target.value }))} />
            </div>
            <div className="adm-form-row">
              <label className="adm-label">Security Group — Bangkok (optional)</label>
              <input className="adm-input" value={acctForm.security_group_id_bk} onChange={e => setAcctForm(f => ({ ...f, security_group_id_bk: e.target.value }))} />
            </div>
            <div className="adm-form-row">
              <label className="adm-label">Security Group — Jakarta (optional)</label>
              <input className="adm-input" value={acctForm.security_group_id_jk} onChange={e => setAcctForm(f => ({ ...f, security_group_id_jk: e.target.value }))} />
            </div>
            <div className="adm-form-row">
              <label className="adm-label">Security Group — Hong Kong (optional)</label>
              <input className="adm-input" value={acctForm.security_group_id_hk} onChange={e => setAcctForm(f => ({ ...f, security_group_id_hk: e.target.value }))} />
            </div>
            {acctErr && <div className="adm-form-err">{acctErr}</div>}
            <div className="adm-modal-footer">
              <button className="adm-btn" onClick={() => setAddAcctModal(false)}>Cancel</button>
              <button className="adm-btn adm-btn-primary" onClick={handleAddAccount} disabled={acctSaving}>
                {acctSaving ? "Adding…" : "Add Account"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="adm-card adm-card-pad" style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#cbd5e1", marginBottom: 14, textTransform: "uppercase", letterSpacing: "1px" }}>Create Ubuntu Instances</div>
        <div className="tencent-create-row" style={{ display: "flex", alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
          <div>
            <label className="adm-label">How many</label>
            <div className="count-field-wrap" style={{ gap: 8 }}>
              <button className="count-step" onClick={() => handleCountChange(String(Math.max(1, parsedCount() - 1)))} disabled={creating}>−</button>
              <input
                className={`count-field${countErr ? " has-err" : ""}`}
                type="text" inputMode="numeric" placeholder="e.g. 20"
                value={countInput} disabled={creating}
                onChange={e => handleCountChange(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && !creating) handleCreate(); }}
              />
              <button className="count-step" onClick={() => handleCountChange(String(parsedCount() + 1))} disabled={creating}>+</button>
            </div>
          </div>
          <div style={{ display: "flex", gap: 7, paddingBottom: 2 }}>
            {[1, 5, 10, 20, 42].map(p => (
              <button key={p} className={`preset-chip${countInput === String(p) ? " active" : ""}`} onClick={() => handleCountChange(String(p))} disabled={creating}>{p}</button>
            ))}
          </div>
          <button className="adm-btn adm-btn-primary" onClick={handleCreate} disabled={creating || !!countErr} style={{ marginLeft: "auto" }}>
            {creating ? "Creating..." : `Create ${parsedCount() || ""} Instance${parsedCount() === 1 ? "" : "s"}`}
          </button>
        </div>
        {countErr && <div className="adm-form-err" style={{ marginTop: 12, display: "inline-block" }}>{countErr}</div>}
      </div>

      {/* Manual Instance Create */}
      <div className="adm-card adm-card-pad" style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#cbd5e1", marginBottom: 4, textTransform: "uppercase", letterSpacing: "1px" }}>Manual Instances</div>
        <div style={{ fontSize: 12, color: "#4a6280", marginBottom: 14 }}>Auto-provision inhe use nahi karega — sirf manually create hoga, orders pe use hoga</div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
          <div>
            <label className="adm-label">Instance Type</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {[
                { key: "spot_8_16",    label: "8c / 16GB",   cap: 20,  color: "#38bdf8", region: "🇧🇷 Sao Paulo" },
                { key: "spot_8_32",    label: "8c / 32GB",   cap: 25,  color: "#fb7185", region: "🇧🇷 Sao Paulo" },
                { key: "spot_16_32",   label: "16c / 32GB",  cap: 40,  color: "#f59e0b", region: "🇧🇷 Sao Paulo" },
                { key: "spot_32_128",  label: "32c / 128GB", cap: 100, color: "#a78bfa", region: "🇧🇷 Sao Paulo" },
                { key: "spot_16_64",     label: "16c / 64GB",  cap: 50,  color: "#34d399", region: "🇧🇷 Sao Paulo" },
                { key: "sg_spot_32_128", label: "32c / 128GB", cap: 100, color: "#22d3ee", region: "🇸🇬 Singapore" },
                { key: "bk_spot_16_64",  label: "16c / 64GB",  cap: 50,  color: "#f472b6", region: "🇹🇭 Bangkok" },
                { key: "bk_spot_4_8",    label: "4c / 8GB",    cap: 10,  color: "#fb923c", region: "🇹🇭 Bangkok" },
              ].map(t => (
                <button
                  key={t.key}
                  onClick={() => setManualType(t.key)}
                  style={{
                    padding: "10px 16px", borderRadius: 10, cursor: "pointer",
                    border: `1px solid ${manualType === t.key ? t.color : "rgba(255,255,255,0.1)"}`,
                    background: manualType === t.key ? `${t.color}18` : "rgba(255,255,255,0.04)",
                    color: manualType === t.key ? t.color : "#64748b",
                    fontWeight: 700, fontSize: 13, fontFamily: "'Outfit', sans-serif",
                    transition: "all .15s",
                  }}
                >
                  <div>{t.label}</div>
                  <div style={{ fontSize: 10, fontWeight: 600, marginTop: 2, opacity: 0.8 }}>{t.region}</div>
                  <div style={{ fontSize: 10, fontWeight: 600, marginTop: 1, opacity: 0.7 }}>cap={t.cap}</div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="adm-label">Count</label>
            <div className="count-field-wrap" style={{ gap: 8 }}>
              <button className="count-step" onClick={() => setManualCount(String(Math.max(1, parseInt(manualCount||1)-1)))} disabled={manualCreating}>−</button>
              <input
                className="count-field"
                type="text" inputMode="numeric" placeholder="1"
                value={manualCount} disabled={manualCreating}
                onChange={e => setManualCount(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && !manualCreating) handleCreateManual(); }}
              />
              <button className="count-step" onClick={() => setManualCount(String(parseInt(manualCount||0)+1))} disabled={manualCreating}>+</button>
            </div>
          </div>
          <button
            className="adm-btn adm-btn-primary"
            onClick={handleCreateManual}
            disabled={manualCreating}
            style={{ marginLeft: "auto", background: manualType === "spot_32_128" ? "linear-gradient(135deg,#7c3aed,#a78bfa)" : manualType === "spot_16_64" ? "linear-gradient(135deg,#059669,#34d399)" : manualType === "sg_spot_32_128" ? "linear-gradient(135deg,#0891b2,#22d3ee)" : manualType === "bk_spot_16_64" ? "linear-gradient(135deg,#db2777,#f472b6)" : manualType === "bk_spot_4_8" ? "linear-gradient(135deg,#c2410c,#fb923c)" : manualType === "spot_8_16" ? "linear-gradient(135deg,#0284c7,#38bdf8)" : manualType === "spot_8_32" ? "linear-gradient(135deg,#e11d48,#fb7185)" : "linear-gradient(135deg,#d97706,#f59e0b)" }}
          >
            {manualCreating ? "Creating..." : `Create Manual Instance`}
          </button>
        </div>
      </div>

      {instances.length > 0 && (
        <div className="tencent-actions" style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: "#94a3b8" }}>
            <input type="checkbox" checked={instances.length > 0 && selected.size === instances.length} onChange={toggleAll} style={{ width: 16, height: 16, cursor: "pointer" }} />
            Select All ({instances.length})
          </label>
          {selected.size > 0 && (
            <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={() => setConfirmSelected(true)}>Delete Selected ({selected.size})</button>
          )}
          <button className="adm-btn adm-btn-danger adm-btn-sm" style={{ marginLeft: "auto" }} onClick={() => setConfirmAll(true)}>Delete All</button>
        </div>
      )}

      {loading && instances.length === 0 && ghostCount === 0 ? (
        <div className="adm-card adm-card-pad adm-empty"><div style={{ fontSize: 28, marginBottom: 8 }}>...</div>Loading instances...</div>
      ) : instances.length === 0 && ghostCount === 0 ? (
        <div className="adm-card adm-card-pad adm-empty"><div style={{ fontSize: 32, marginBottom: 8 }}>☁️</div>No Ubuntu instances yet. Create one above.</div>
      ) : (
        <div className="tencent-grid">
          {Array.from({ length: ghostCount }).map((_, i) => <GhostCard key={`ghost-${i}`} />)}
          {instances.map(inst => {
            const isProvisioning = inst.provision_stage && inst.provision_stage !== "online" && inst.provision_stage !== "failed";
            const isFailed       = inst.provision_stage === "failed";
            return (
              <div
                key={inst.instance_id}
                className={`inst-card${selected.has(inst.instance_id) ? " selected" : ""}${isProvisioning ? " provisioning" : ""}${isFailed ? " failed" : ""}`}
              >
                {/* Header row: checkbox + name + status */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                    <input type="checkbox" checked={selected.has(inst.instance_id)} onChange={() => toggleSelect(inst.instance_id)} style={{ width: 15, height: 15, cursor: "pointer" }} />
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#e2e8f0" }}>{inst.name}</span>
                  </label>
                  <StatusBadge status={inst.status} />
                </div>

                {/* Account + location badge row */}
                <div style={{ marginBottom: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <AccountBadge label={inst.account_label} />
                  <span className="adm-badge adm-badge-gray">{regionLabel(inst.region)}</span>
                </div>

                <ProvisionBar stage={inst.provision_stage} pct={inst.provision_pct} error={inst.provision_error} />

                <div style={{ marginBottom: 10 }}>
                  <div className="inst-meta-label">Public IP</div>
                  {inst.public_ip ? <span className="inst-ip">{inst.public_ip}</span> : <span style={{ color: "#4a6280", fontSize: 13 }}>Pending...</span>}
                </div>
                <div style={{ marginBottom: 10 }}>
                  <div className="inst-meta-label">Instance ID</div>
                  <span className="inst-id">{inst.instance_id}</span>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
                  <span className="adm-badge" style={{
                    background: `${instanceTypeMeta(inst.instance_type).color}26`,
                    color: instanceTypeMeta(inst.instance_type).color,
                    border: `1px solid ${instanceTypeMeta(inst.instance_type).color}4d`,
                  }}>
                    {instanceTypeMeta(inst.instance_type).label} · {inst.instance_type}
                  </span>
                  {inst.created_time && <span className="adm-badge adm-badge-gray">{new Date(inst.created_time).toLocaleDateString()}</span>}
                </div>
                {inst.provision_error && (
                  <button
                    className="adm-btn adm-btn-sm"
                    style={{ width: "100%", justifyContent: "center", marginBottom: 8 }}
                    onClick={() => handleRetry(inst.instance_id)}
                    disabled={retryingIds.has(inst.instance_id)}
                  >
                    {retryingIds.has(inst.instance_id) ? "Retrying…" : "↻ Retry"}
                  </button>
                )}
                <button
                  className="adm-btn adm-btn-danger adm-btn-sm"
                  style={{ width: "100%", justifyContent: "center" }}
                  onClick={() => setConfirmDelete({ id: inst.instance_id, name: inst.name })}
                  disabled={deletingIds.has(inst.instance_id)}
                >
                  {deletingIds.has(inst.instance_id) ? "Deleting..." : "Terminate Instance"}
                </button>
              </div>
            );
          })}
        </div>
      )}
      {/* Confirm single delete */}
      <ConfirmModal
        open={!!confirmDelete}
        title="Terminate Instance?"
        message={<>Are you sure you want to terminate <strong style={{color:"#f87171"}}>{confirmDelete?.name || confirmDelete?.id}</strong>? This cannot be undone.</>}
        confirmLabel="Terminate"
        onConfirm={() => handleDeleteOne(confirmDelete?.id)}
        onCancel={() => setConfirmDelete(null)}
      />
      {/* Confirm selected delete */}
      <ConfirmModal
        open={confirmSelected}
        title={`Delete ${selected.size} Instance(s)?`}
        message={<>This will permanently terminate <strong style={{color:"#f87171"}}>{selected.size} selected instance(s)</strong>. This cannot be undone.</>}
        confirmLabel="Delete Selected"
        onConfirm={handleDeleteSelected}
        onCancel={() => setConfirmSelected(false)}
      />
    </div>
  );
}// Thu Aug 13 07:27:35 AM UTC 2026
